// @ts-strict-ignore
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { graphql } from "gatsby";
import styles from "./list-block.module.less";
import borderPatterns from "styles/theming/border-patterns.module.less";
import { motion } from "framer-motion";
import ShowListItemOverlay from "components/block-elements/show-list-item-overlay/show-list-item-overlay";
import RideListItem from "components/block-elements/ride-list-item/ride-list-item";
import RestaurantListItem from "components/block-elements/restaurant-list-item/restaurant-list-item";
import GeneralContentListItem from "components/block-elements/general-content-list-item/general-content-list-item";
import GeneralContentAdListItem from "components/block-elements/general-content-ad-list-item/general-content-ad-list-item";
import EventAttributeStore from "components/stores/event-attribute-store";
import SiteConfigurationStore from "components/stores/site-configuration-store";
import {
  isArray,
  createDate,
  BackgroundContentCardBlock,
  ScheduleStore,
  EventAggregator,
  ParkFoodService,
  PrimaryButton,
  PrimaryButtonLink,
  CheckboxButtons,
  RadioButtons,
  CustomerDisplayWeb,
  Carousel,
  SimplifyGatsbyQuery,
  useIsParkOpenNow,
  isNonEmptyArray,
  useHashState,
  useFetchQueueTime,
} from "@parksandresorts/core";
import Animation from "components/block-elements/animation/animation";
import RestaurantList from "components/blocks/list-block/restaurant-list";

const LARGE_BREAKPOINT = 1024;
const MEDIUM_BREAKPOINT = 768;
const PASSED_SHOW = "Tidigare spelningar";

enum Device {
  DEVICE_DESKTOP = "DEVICE_DESKTOP",
  DEVICE_TABLET = "DEVICE_TABLET",
  DEVICE_MOBILE = "DEVICE_MOBILE",
}

const isString = (s: string | null | boolean | undefined): s is string => !!s;

enum BlockTypes {
  SHOW_BLOCK = "ContentfulShowBlock",
  RIDE_BLOCK = "ContentfulRideBlock",
  RESTAURANT_BLOCK = "ContentfulRestaurantBlock",
  GENERAL_CONTENT_BLOCK = "ContentfulGeneralContentBlock",
}

// These need to be aligned with the values of displayMode in Contentful
enum DisplayMode {
  Default = "Default",
  Carousel = "Carousel",
  BackgroundContentCard = "Background Content Card",
}
type DisplayModeMobile = DisplayMode | "Same as desktop";

const ListBlock = (
  props: SimplifyGatsbyQuery<Queries.ListBlockFragment> & {
    blockInOverlay?: boolean;
    blockInOverlayIndex?: number;
  },
) => {
  const componentTypes = {
    ContentfulShowBlock: {
      carouselComponent: ShowListItemOverlay,
      listComponent: ShowListItemOverlay,
      css: styles.showListItemOverlay,
      mobileCompactList: true,
      rowSize: {
        small: 1,
        medium: 2,
        large: 3,
      },
    },
    ContentfulRideBlock: {
      carouselComponent: RideListItem,
      listComponent: RideListItem,
      css: styles.rideListItem,
      mobileCompactList: true,
      rowSize: {
        small: 1,
        medium: 2,
        large: 3,
      },
    },
    ContentfulRestaurantBlock: {
      carouselComponent: RestaurantListItem,
      listComponent: RestaurantListItem,
      css: styles.listItem,
      mobileCompactList: true,
      rowSize: {
        small: 1,
        medium: 2,
        large: 3,
      },
    },
    ContentfulGeneralContentBlock: {
      carouselComponent: GeneralContentListItem,
      listComponent: GeneralContentListItem,
      listAdComponent: GeneralContentAdListItem,
      css: styles.listItem,
      mobileCompactList: false,
      rowSize: {
        small: 1,
        medium: 2,
        large: 3,
      },
    },
  };

  const {
    title,
    lists,
    filterAllLabel,
    enableFilter,
    filterHeadline,
    showLengthFilter,
    lengthFilterHeadline,
    componentTheme,
    borderPattern,
    isShowList,
    displayRestaurantWaitingOrders,
    restaurantsLinksToPej,
  } = props;

  const preamble = props?.preamble?.preamble;
  const showFilterLabel = SiteConfigurationStore.getLabel("SHOW_FILTER");
  const primaryButtonLink = props?.primaryButtonLink?.internalLink?.slug || props?.primaryButtonLink?.externalUrl;
  const primaryButtonLabel = props?.primaryButtonLink?.title;

  const filterTags = useMemo(() => {
    if (isNonEmptyArray(props?.filters) && filterAllLabel) {
      return [{ id: "filterAll", title: filterAllLabel }, ...props.filters];
    }
    return props?.filters;
  }, [props?.filters, filterAllLabel]);

  const secondaryButtonLink = props?.secondaryButtonLink?.internalLink?.slug || props?.secondaryButtonLink?.externalUrl;
  const secondaryButtonLabel = props?.secondaryButtonLink?.title;

  const noEventData = SiteConfigurationStore.getLabel("NO_EVENT_DATA");

  const extraFilter = null;

  const [{ filter: activeFilterFromHash, lengthFilter }, setFilter] = useHashState(["filter", "lengthFilter"]);
  const activeFilter = useMemo(
    () => (!isNonEmptyArray(activeFilterFromHash) && filterAllLabel ? [filterAllLabel] : activeFilterFromHash),
    [activeFilterFromHash, filterAllLabel],
  );
  const lengthFilterValue = lengthFilter[0] || "-1";
  const lengthFilterActive = lengthFilterValue !== "-1";

  const setActiveFilter = useCallback(
    (value: string[]) => {
      setFilter({ filter: value });
    },
    [setFilter],
  );
  const setLengthFilterValue = useCallback(
    (value: string) => {
      const lengthFilter = value === "-1" ? [] : [value];
      setFilter({ lengthFilter });
    },
    [setFilter],
  );

  const [activeDevice, setActiveDevice] = useState<Device | null>(null);
  const [displayMobileFilter, setDisplayMobileFilter] = useState(false);
  const [displayMobileLengthFilter, setDisplayMobileLengthFilter] = useState(false);
  const [showsWithDates, setShowsWithDates] = useState({ listObjects: [] as any[] });
  const [allCustomerDisplays, setAllCustomerDisplays] = useState<Array<CustomerDisplayWeb>>([]);

  const fetchQueueStatusInterval = useRef<number | null>(null);
  const isParkOpenNow = useIsParkOpenNow();
  const queueTimes = useFetchQueueTime({ updateInterval: 30_000 });

  const hasRestaurantBlocks = useMemo(
    () =>
      lists?.some((list) => list?.listObjects?.some((item) => item?.__typename === BlockTypes.RESTAURANT_BLOCK)) ??
      false,
    [lists],
  );

  const hasRideBlocks = useMemo(
    () => lists?.some((list) => list?.listObjects?.some((item) => item?.__typename === BlockTypes.RIDE_BLOCK)) ?? false,
    [lists],
  );

  const shouldRenderRestaurantFilterList = hasRestaurantBlocks && enableFilter;

  useEffect(() => {
    const windowWidthChanged = (windowWidth: number) => {
      if (windowWidth >= LARGE_BREAKPOINT) {
        if (activeDevice !== Device.DEVICE_DESKTOP) {
          setActiveDevice(Device.DEVICE_DESKTOP);
        }
      } else if (window.innerWidth >= MEDIUM_BREAKPOINT) {
        if (activeDevice !== Device.DEVICE_TABLET) {
          setActiveDevice(Device.DEVICE_TABLET);
        }
      } else {
        if (activeDevice !== Device.DEVICE_MOBILE) {
          setActiveDevice(Device.DEVICE_MOBILE);
        }
      }
    };
    const onResizeWindowWidthSubscription = EventAggregator.subscribe("onResizeWindowWidth", windowWidthChanged);
    windowWidthChanged(EventAttributeStore.getCurrentWindowWidth());

    return () => {
      onResizeWindowWidthSubscription.remove();
    };
  }, [activeDevice, setActiveDevice]);

  useEffect(() => {
    const allFilterTags = extraFilter ? [...(filterTags ?? []), extraFilter] : filterTags;
    const isFiltersValid = (filtersFromHash: string[]) => {
      if (!isNonEmptyArray(allFilterTags)) {
        return true;
      }

      return filtersFromHash.every((filter) => allFilterTags.some((filterItem) => filterItem?.title === filter));
    };
    const getDefaultFilters = () => {
      if (isShowList) {
        return allFilterTags?.map((filter) => filter?.title).filter(isString) ?? [];
      }
      return [];
    };

    const defaults = getDefaultFilters();
    if (!isFiltersValid(activeFilter) || (defaults.length > 0 && activeFilter.length === 0)) {
      setActiveFilter(defaults);
    }
  }, [activeFilter, setActiveFilter, filterTags, filterAllLabel, isShowList]);

  useEffect(() => {
    const allowedLengthFilters = ["-1", "99", "100", "110", "120", "140"];
    if (!allowedLengthFilters.includes(lengthFilterValue)) {
      setLengthFilterValue("-1");
    }
  }, [setLengthFilterValue, lengthFilterValue]);

  useEffect(() => {
    if (isShowList) {
      const shows =
        lists?.flatMap(
          (list) => list?.listObjects?.map((item) => item && "contentful_id" in item && item.contentful_id),
        ) ?? [];
      const showIds = [...new Set(shows.filter(isString))];

      ScheduleStore.getEventsForShowIds(showIds).then((response) => {
        const showsWithDates = addFilterAndConvertToList(response);
        setShowsWithDates(showsWithDates);
      });
    }
  }, [isShowList, lists]);

  useEffect(() => {
    const fetchQueueStatus = (terminalIds: Array<number>) => {
      ParkFoodService.getQueueStatusForWeb(terminalIds)
        .then((newCustomerDisplayItems) => {
          setAllCustomerDisplays(newCustomerDisplayItems);
        })
        .catch((err) => {
          EventAggregator.publish("trackException", {
            exception: err,
            customMessage: "ListBlock getQueueStatusForWeb",
          });
        });
    };

    if (displayRestaurantWaitingOrders && isArray(lists)) {
      const foodTerminalIds: Array<number> = [];
      lists?.forEach((list) => {
        const listObjects = list?.listObjects ?? [];
        listObjects?.forEach((item) => {
          if (item?.__typename === "ContentfulRestaurantBlock" && item?.foodTerminalId) {
            foodTerminalIds.push(item.foodTerminalId);
          }
        });
      });

      if (foodTerminalIds.length >= 1 && fetchQueueStatusInterval.current === null) {
        fetchQueueStatus(foodTerminalIds);
        fetchQueueStatusInterval.current = setInterval(
          () => fetchQueueStatus(foodTerminalIds),
          30000,
        ) as unknown as number;
      }
    }
  }, [displayRestaurantWaitingOrders, lists, allCustomerDisplays]);

  useEffect(() => {
    return () => {
      clearInterval(fetchQueueStatusInterval.current ?? undefined);
    };
  }, []);

  const addFilterAndConvertToList = (showArray) => {
    const addedFilters = showArray.map((item) => {
      const now = createDate();
      const endTime =
        isArray(item.events) && item.events[0] && item.events[0].startDateAndTime
          ? createDate(item.events[0].startDateAndTime)
          : null;
      if (endTime) {
        endTime.setHours(endTime.getHours() + 2);
      }

      if (!endTime) {
        return item;
      }

      if (now > endTime) {
        const newTags = item.tags === null ? [{ title: PASSED_SHOW }] : [...item.tags, { title: PASSED_SHOW }];
        const toReturn = { ...item, tags: newTags };

        return toReturn;
      }
      return item;
    });

    const list = {
      listObjects: addedFilters,
    };

    return list;
  };

  const getRowSizeForComponentType = (rowSizeConfig) => {
    const width = EventAttributeStore.getCurrentWindowWidth();

    if (width < MEDIUM_BREAKPOINT) {
      return rowSizeConfig.small;
    } else if (width > MEDIUM_BREAKPOINT && width < LARGE_BREAKPOINT) {
      return rowSizeConfig.medium;
    } else {
      return rowSizeConfig.large;
    }
  };

  const renderButtons = () => {
    return (
      <div className={[styles.buttonWrapper].join(" ")}>
        {primaryButtonLink && primaryButtonLabel && (
          <PrimaryButtonLink label={primaryButtonLabel} url={primaryButtonLink} className={styles.button} />
        )}
        {secondaryButtonLink && secondaryButtonLabel && (
          <PrimaryButtonLink
            complementary
            label={secondaryButtonLabel}
            url={secondaryButtonLink}
            className={styles.button}
          />
        )}
      </div>
    );
  };

  const renderList = (list: NonNullable<NonNullable<typeof lists>[0]>) => {
    if (!(list.listObjects instanceof Array && list.listObjects.length > 0)) {
      return null;
    }

    let filteredList = list.listObjects.filter((item: any) => {
      if (item.foodTerminalId && displayRestaurantWaitingOrders) {
        return item;
      } else {
        if (item.tags) {
          return filterItemsByTags(item.tags);
        }
        return filterItemsByTags(item.tag);
      }
    });

    if (lengthFilterActive) {
      filteredList = filteredList.filter((item) => {
        if (item && "minimumHeightInteger" in item && item.minimumHeightInteger) {
          return item.minimumHeightInteger <= parseInt(lengthFilterValue);
        }
        return true;
      });
    }

    if (filteredList.length === 0) {
      return null;
    }

    const displayModeDesktop = list.displayMode as DisplayMode;
    const displayModeMobile = list.displayModeMobile as DisplayModeMobile;
    const displayMode =
      activeDevice === Device.DEVICE_MOBILE && displayModeMobile !== "Same as desktop"
        ? displayModeMobile
        : displayModeDesktop;

    if (displayMode === DisplayMode.BackgroundContentCard) {
      const cards = filteredList.map((item) => {
        if (item?.__typename !== "ContentfulGeneralContentBlock") {
          throw new Error(
            "When using Display Mode = BackgroundContentCard, all items must be ContentfulGeneralContentBlock",
          );
        }

        const link = item.link ?? {
          internalLink: item.pageLink,
          externalUrl: undefined,
          title: undefined,
        };
        return {
          key: item.id,
          imageData: item.imageInList?.gatsbyImageData ?? undefined,
          title: item.title ?? undefined,
          shortDescription: item.shortDescriptionInList ?? undefined,
          internalLink: link.internalLink?.slug ?? undefined,
          externalLink: link.externalUrl ?? undefined,
          linkLabel: link.title ?? undefined,
          label: item.label ?? undefined,
          iconClass: "icon-keyboard_arrow_right",
        };
      });
      return (
        <BackgroundContentCardBlock
          key={list.id + "-" + filteredList.length}
          title={list.headline ?? ""}
          cards={cards}
        />
      );
    }
    // Made this a variable so that we can use a switch for exhaustive check
    const listElement: JSX.Element = (() => {
      switch (displayMode) {
        case DisplayMode.Carousel:
          return renderCarouselList(filteredList);
        case DisplayMode.Default:
          return renderCardList(filteredList);
      }
    })();
    return (
      <div className={styles.list} key={list.id + "-" + filteredList.length}>
        {list.headline && <h3 className={styles.listHeadline}>{list.headline}</h3>}

        {list.ingress && <p className={["p", styles.listIngress].join(" ")}>{list.ingress}</p>}

        {listElement}
      </div>
    );
  };

  const renderShowList = (list) => {
    if (!(list.listObjects instanceof Array && list.listObjects.length > 0)) {
      return (
        <div className={styles.noShowsLoaded}>
          <h2>{noEventData}</h2>
        </div>
      );
    }

    const filteredList = list.listObjects.filter((item) => {
      if (item.tags) {
        return filterConcertItemsByTags(item.tags);
      }
      return filterConcertItemsByTags(item.tag);
    });

    if (!isArray(filteredList)) {
      return null;
    }

    return (
      <ul key={list.id} className={[styles.grid, styles.showListGrid].join(" ")}>
        {filteredList.map((item, index) => {
          return (
            <li key={item.id + index} className={styles.showListItemOverlay}>
              <ShowListItemOverlay {...item} />
            </li>
          );
        })}
      </ul>
    );
  };

  const getLinkDataForRestaurantItem = (item) => {
    if (!item || (!item.externalPejLink && !item.pageLink)) {
      return null;
    }
    if (item.externalPejLink && restaurantsLinksToPej) {
      return {
        linkUrl: item.externalPejLink,
        isExternalLink: true,
      };
    }
    if (item.pageLink && !restaurantsLinksToPej) {
      return {
        linkUrl: item.pageLink.slug,
        isExternalLink: false,
      };
    }
    return null;
  };

  const enrichRestaurantItems = (list: ListObjects) => {
    return list.map((item) => {
      //There are certain features we only want present if we are on a "mat-online" page.
      //we currently use the "restaurantsLinksToPej" as a proxy for being on a "mat-online" page.
      const isContextParkFood = !!restaurantsLinksToPej;

      const displayData = allCustomerDisplays.find(
        (customerDisplay) => customerDisplay.terminalId === item.foodTerminalId,
      );
      const shouldShowWarningModal = displayData?.shouldWarnForLongQueue;
      const shouldShowBlockingModal = displayData?.shouldStopWebOrders;
      const waitTimeLabel = displayData?.waitTimeLabelWeb;
      const approximateWaitTime = displayData?.approximateWaitTime;
      const linkData = getLinkDataForRestaurantItem(item);

      //We don't want to show the "Beställ online"-label when we are on a "mat-online" page.
      //we currently use the "restaurantLinksToPej" as a proxy for being on a "mat-online" page.
      const shouldShowLabelUnderImage = !isContextParkFood;
      return {
        ...item,
        shouldShowWarningModal,
        shouldShowBlockingModal,
        waitTimeLabel,
        approximateWaitTime,
        linkData,
        shouldShowLabelUnderImage,
        isParkOpenNow,
      };
    });
  };

  const addAttractionQueueTimesToList = (list) => {
    return list.map((item) => {
      const currentQueueTime = queueTimes?.find((ride) => item.rideId === ride.attractionId);
      if (!currentQueueTime) {
        return { ...item };
      }
      return {
        ...item,
        attractionQueueTime: currentQueueTime,
      };
    });
  };

  const renderCardList = (list) => {
    let counter = 0;

    let transformedList = enrichRestaurantItems(list);
    if (isParkOpenNow && hasRideBlocks) {
      transformedList = addAttractionQueueTimesToList(transformedList);
    }
    const isAllItemsShowBlocks = transformedList.every((item) => item.__typename === BlockTypes.SHOW_BLOCK);

    return (
      <ul className={[styles.grid, isAllItemsShowBlocks ? styles.showListGrid : ""].join(" ")}>
        {transformedList.map((item, index) => {
          const componentConfig = componentTypes[item.__typename];
          const isAd = item.isAdInList ? item.isAdInList : false;

          if (!componentConfig) {
            return null;
          }

          const rowSize = getRowSizeForComponentType(componentConfig.rowSize);
          if (counter === rowSize) {
            counter = 1;
          } else {
            counter++;
          }

          const ComponentType = isAd ? componentConfig.listAdComponent : componentConfig.listComponent;
          const mobileCompactList = componentConfig.mobileCompactList;

          return (
            <li key={item.id + index} className={[componentConfig.css, styles.gridCell].join(" ")}>
              <Animation
                disableOnScrollCheck={props.blockInOverlay}
                applyAnimation={props.blockInOverlay}
                yAnimationOffsetMobile={60}
                yAnimationOffset={100}
                animationDelay={100 * counter}
                animationDuration={800}
                animationType="fadeIn"
                className={styles.gridCell}
              >
                <ComponentType mobileCompactList={mobileCompactList} {...item} />
              </Animation>
            </li>
          );
        })}
      </ul>
    );
  };

  type ListObjects = SimplifyGatsbyQuery<Queries.ListBlockFragment>["lists"][number]["listObjects"];
  const renderCarouselList = (list: ListObjects) => {
    let transformedItems = enrichRestaurantItems(list);
    if (isParkOpenNow && hasRideBlocks) {
      transformedItems = addAttractionQueueTimesToList(transformedItems);
    }
    const mappedItems = transformedItems.map((item) => {
      const componentConfig = componentTypes[item.__typename];
      const isAd = item.isAdInList ? item.isAdInList : false;

      if (!componentConfig) {
        return null;
      }

      const ComponentType = isAd ? componentConfig.listAdComponent : componentConfig.carouselComponent;

      return <ComponentType key={item.id} {...item} />;
    });

    return <Carousel navDots={true}>{mappedItems}</Carousel>;
  };

  const isAnyTagFilterSelected = () => {
    if ((filterAllLabel && activeFilter.indexOf(filterAllLabel) !== -1) || activeFilter.length === 0) {
      return false;
    }

    return true;
  };

  const filterItemsByTags = (tags: Array<any>) => {
    if (!isAnyTagFilterSelected()) {
      return true;
    }

    const cleanedTags = tags ? tags.map((entry: any) => entry.title) : [];

    if (!(cleanedTags instanceof Array && cleanedTags.length > 0)) {
      return false;
    }

    for (const tag of activeFilter) {
      if (cleanedTags.indexOf(tag) !== -1) {
        return true;
      }
    }

    return false;
  };

  const filterConcertItemsByTags = (tags) => {
    const cleanedTags = tags ? tags.map((entry) => entry.title) : [];

    if (cleanedTags.indexOf(PASSED_SHOW) !== -1) {
      if (activeFilter.indexOf(PASSED_SHOW) === -1) {
        return false;
      }
    }

    if (!isAnyTagFilterSelected()) {
      return true;
    }

    if (!(cleanedTags instanceof Array && cleanedTags.length > 0)) {
      return false;
    }

    for (const tag of activeFilter) {
      if (cleanedTags.indexOf(tag) !== -1) {
        return true;
      }
    }

    return false;
  };

  const shouldRenderFilterBlock = () => {
    return enableFilter && isArray(filterTags) && !shouldRenderRestaurantFilterList;
  };

  const filterChanged = (selectedValues: string, event?: any) => {
    let valuesArray = selectedValues == "" ? [] : selectedValues.split(", ");
    if (filterAllLabel) {
      // check if another filter has been selected and the filterAllLabel was previously selected
      // this is necessary since the new value is only appended to the array, it does not remove the filterAllLabel
      if (valuesArray.some((t) => t !== filterAllLabel) && activeFilter.includes(filterAllLabel)) {
        valuesArray = valuesArray.filter((value) => value !== filterAllLabel);
      } // check if the filterAllLabel has been selected but was not previously selected
      else if (valuesArray.includes(filterAllLabel)) {
        // We default to the filterAllLabel if no other filter is selected, so this cleans up the filter hash
        valuesArray = [];
      }
    }

    setActiveFilter(valuesArray);

    if (valuesArray.length > 0) {
      EventAggregator.publish("trackEvent", {
        category: "Filter List",
        action: "Selected filters",
        label: selectedValues,
      });
    }

    if (event && event.target.checked) {
      EventAggregator.publish("trackEvent", {
        category: "Filter List",
        action: "Clicked filter",
        label: event.target.value,
      });
    }
  };

  const lengthFilterChanged = (value: string) => {
    setLengthFilterValue(value);

    EventAggregator.publish("trackEvent", {
      category: "Filter List",
      action: "Clicked Length Filter button",
      label: value,
    });
  };

  const calculateLengthFilterActive = (filterValue: number) => {
    return parseInt(lengthFilterValue) > filterValue || parseInt(lengthFilterValue) === filterValue;
  };

  const renderFilterButtons = () => {
    const tagsToOptions = filterTags
      ?.filter((tag) => tag?.title)
      .map((tag) => {
        return { value: tag.title, label: tag.title };
      });

    if (tagsToOptions && tagsToOptions?.length > 0) {
      return (
        <motion.div
          initial={{ height: isShowList ? "auto" : 0 }} // Show lists should start expanded
          animate={{ height: displayMobileFilter ? "auto" : 0 }}
          className={[styles.filterList, displayMobileFilter ? styles.visible : ""].join(" ")}
        >
          {filterHeadline && <h6 className={styles.filterHeadline}>{filterHeadline}</h6>}
          <CheckboxButtons
            wrapperClassName={styles.checkboxWrapper}
            checkboxClassName={styles.checkbox}
            options={tagsToOptions}
            value={activeFilter?.join(", ")}
            onChange={(name, value: string) => filterChanged(value)}
          />
        </motion.div>
      );
    }
    return null;
  };

  const renderLengthFilterButtons = () => {
    const filterLengths = [
      {
        id: "-1",
        key: "-1",
        value: -1,
        label: "Alla",
      },
      {
        id: "99",
        key: "99",
        value: 99,
        label: "0-99 cm",
      },
      {
        id: "100",
        key: "100",
        value: 100,
        label: "100 cm",
      },
      {
        id: "110",
        key: "110",
        value: 110,
        label: "110 cm",
      },
      {
        key: "120",
        id: "120",
        value: 120,
        label: "120 cm",
      },
      {
        id: "140",
        key: "140",
        value: 140,
        label: "140 cm",
      },
    ];

    const radioButtons = filterLengths.map((filter) => {
      const shouldBeActive = calculateLengthFilterActive(filter.value);
      return {
        value: filter.value,
        label: (
          <div className={styles.radioLabelWrapper}>
            <motion.span
              key={filter.id}
              className={[styles.iconWrapper, shouldBeActive ? styles.activeIcon : styles.inactiveIcon].join(" ")}
            >
              {lengthFilterValue === filter.id && <span className={[styles.icon, "icon-check"].join(" ")} />}
            </motion.span>
            <span className={[styles.radioLabel, "p_tiny"].join(" ")}>{filter.label}</span>
          </div>
        ),
      };
    });

    return (
      <RadioButtons
        onChange={(name: string, value: string) => lengthFilterChanged(value)}
        options={radioButtons}
        name="lengthFilter"
        value={lengthFilterValue}
        wrapperClassName={styles.radioWrapper}
        radioItemsWrapperClassName={styles.radioItemsWrapper}
      />
    );
  };

  const filteredHeadline: string = useMemo(() => {
    const activeFilterCount = activeFilter.filter((t) => t !== filterAllLabel).length;
    const filterAmount = activeFilterCount > 0 ? `(${activeFilterCount}) ` : "";

    return filterHeadline ? `${filterAmount}${filterHeadline}` : `${filterAmount}${showFilterLabel}`;
  }, [activeFilter, filterAllLabel, filterHeadline, showFilterLabel]);

  return (
    <section
      className={[
        `block block--no-margin`,
        borderPattern ? "block--with-border" : "",
        borderPatterns[borderPattern],
      ].join(" ")}
      data-contentful-id={props.contentful_id}
    >
      <div className={[styles.wrapper, isShowList ? styles.wrapperExtraPadded : ""].join(" ")}>
        {!shouldRenderFilterBlock() && title && (
          <div className={styles.headerWrapper}>
            <h2 className={["h1", styles.blockHeader].join(" ")}>{title}</h2>
            {preamble && <p className={["preamble", styles.blockPreamble].join(" ")}>{preamble}</p>}
          </div>
        )}
        {shouldRenderRestaurantFilterList && (
          <RestaurantList
            lists={lists}
            filterGroups={props.filterGroupFilters}
            quickFilters={props.filters}
            filterHeadline={filterHeadline}
            filterParagraph={props.filterIntroParagraph}
            displayRestaurantWaitingOrders={props.displayRestaurantWaitingOrders}
            restaurantsLinksToPej={props.restaurantsLinksToPej}
          />
        )}
        {shouldRenderFilterBlock() && (
          <div
            className={[
              styles.filterBlock,
              isShowList ? ["block--max-width-extra", styles.showListFilter].join(" ") : "block--max-width",
            ].join(" ")}
          >
            <div>
              {title && <h2 className={styles.filterHeader}>{title}</h2>}
              {preamble && <p className={["preamble", styles.filterPreamble].join(" ")}>{preamble}</p>}
              <PrimaryButton
                className={[styles.toggleFilterButton, displayMobileFilter ? styles.filterVisible : undefined].join(
                  " ",
                )}
                label={filteredHeadline}
                iconLeft={!activeFilter.length ? "icon-filter-list" : undefined}
                iconRight={displayMobileFilter ? "icon-chevron-up bold" : "icon-chevron-down bold"}
                onClick={() => setDisplayMobileFilter(!displayMobileFilter)}
              />
              {renderFilterButtons()}
            </div>
            {showLengthFilter && (
              <div className={styles.lengthFilterWrapper}>
                <PrimaryButton
                  className={[
                    styles.toggleFilterButton,
                    displayMobileLengthFilter ? styles.filterVisible : undefined,
                  ].join(" ")}
                  label={lengthFilterHeadline ? lengthFilterHeadline : "Filtrera på längd"}
                  iconLeft="icon-filter-list"
                  iconRight={displayMobileLengthFilter ? "icon-chevron-up bold" : "icon-chevron-down bold"}
                  onClick={() => setDisplayMobileLengthFilter(!displayMobileLengthFilter)}
                />
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: displayMobileLengthFilter ? "auto" : 0 }}
                  className={[
                    styles.filterList,
                    styles.lengthFilter,
                    displayMobileLengthFilter ? styles.visible : "",
                  ].join(" ")}
                >
                  {lengthFilterHeadline && <h6 className={styles.filterHeadline}>{lengthFilterHeadline}</h6>}
                  {renderLengthFilterButtons()}
                </motion.div>
              </div>
            )}
          </div>
        )}
        <div>
          <div
            className={[
              styles.listInnerWrapper,
              componentTheme ? `component-theme component-theme-${componentTheme} ${componentTheme}` : "",
            ].join(" ")}
          >
            {isShowList ? (
              <div className="block--max-width-extra">{renderShowList(showsWithDates)}</div>
            ) : (
              lists &&
              lists.length &&
              !shouldRenderRestaurantFilterList && (
                <div className={isShowList ? "block--max-width-extra" : "block--max-width"}>
                  {lists.map((list) => renderList(list))}
                </div>
              )
            )}
          </div>
          {renderButtons()}
        </div>
      </div>
    </section>
  );
};
export default ListBlock;

export const tagFragment = graphql`
  fragment Tag on ContentfulTag {
    title
    id
  }
`;

export const fragmentListBlock = graphql`
  fragment ListBlock on ContentfulListBlock {
    contentful_id
    id
    title
    filterAllLabel
    isShowList
    enableFilter
    filterHeadline
    showLengthFilter
    lengthFilterHeadline
    componentTheme
    borderPattern
    filters {
      id
      title
    }
    filterGroupFilters {
      id
      headline
      listObjects {
        __typename
        ...Tag
      }
    }
    filterIntroParagraph {
      raw
    }
    lists {
      id
      headline
      ingress
      displayMode
      displayModeMobile
      listObjects {
        __typename
        ...ShowBlock
        ...RestaurantBlock
        ...RideBlock
        ...GeneralContentBlock
        ...AccommodationBlock
      }
    }
    preamble {
      preamble
    }
    primaryButtonLink {
      title
      internalLink {
        slug
      }
      externalUrl
    }
    secondaryButtonLink {
      title
      internalLink {
        slug
      }
      externalUrl
    }
    displayRestaurantWaitingOrders
    restaurantsLinksToPej
  }
`;
